/**
 * MyMouvPromoCodesPanel — Organizer panel to generate / list / delete
 * single-use promo codes for a specific event. A redeemed code issues a
 * FREE ticket (100 % off) and is auto-locked forever.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Ticket, Copy, Trash2, Plus, X } from 'lucide-react';

const StatusPill = ({ status }) => {
  const map = {
    active: 'bg-emerald-500/20 text-emerald-200 border-emerald-400/40',
    used: 'bg-blue-500/20 text-blue-200 border-blue-400/40',
    expired: 'bg-white/10 text-white/60 border-white/20',
  };
  const label = { active: 'Actif', used: 'Utilisé', expired: 'Expiré' }[status] || status;
  return (
    <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border ${map[status] || map.expired}`}>
      {label}
    </span>
  );
};

const MyMouvPromoCodesPanel = ({ event, onClose }) => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState(1);
  const [ttypeId, setTtypeId] = useState('');

  const load = useCallback(async () => {
    if (!event?.id) return;
    setLoading(true);
    try {
      const r = await axios.get(`/api/mymouv/organizer/events/${event.id}/promo-codes`);
      setCodes(r.data?.codes || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  }, [event?.id]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      const body = { count: Math.max(1, Math.min(50, Number(count) || 1)) };
      if (ttypeId) body.ticket_type_id = ttypeId;
      const r = await axios.post(`/api/mymouv/organizer/events/${event.id}/promo-codes`, body);
      toast.success(`${r.data?.created || 0} code(s) généré(s)`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer ce code ?')) return;
    try {
      await axios.delete(`/api/mymouv/organizer/events/${event.id}/promo-codes/${id}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const copy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success('Code copié');
    } catch {
      toast.error('Copie impossible');
    }
  };

  const activeCount = codes.filter(c => c.status === 'active').length;
  const usedCount = codes.filter(c => c.status === 'used').length;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="mymouv-promo-panel">
      <div className="my-6 max-w-2xl w-full bg-gray-900 rounded-3xl border border-white/10 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold flex items-center gap-2"><Ticket className="w-5 h-5 text-fuchsia-400" /> Codes promo</h3>
            <p className="text-xs text-white/60 mt-0.5">{event?.title}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10" data-testid="mymouv-promo-close"><X className="w-5 h-5" /></button>
        </div>

        {/* How it works */}
        <div className="rounded-xl bg-fuchsia-500/10 border border-fuchsia-400/30 p-3 text-xs text-fuchsia-100 mb-4">
          Un code promo offre <strong>1 billet gratuit à 100 %</strong> (usage unique). Il devient inutilisable dès qu&apos;un billet a été émis avec. Validité automatique : jusqu&apos;à 1 jour après l&apos;événement.
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="rounded-lg bg-white/5 p-3 text-center">
            <div className="text-xs text-white/50">Total</div>
            <div className="text-lg font-bold" data-testid="mymouv-promo-total">{codes.length}</div>
          </div>
          <div className="rounded-lg bg-emerald-500/10 p-3 text-center">
            <div className="text-xs text-emerald-200/70">Actifs</div>
            <div className="text-lg font-bold text-emerald-300" data-testid="mymouv-promo-active">{activeCount}</div>
          </div>
          <div className="rounded-lg bg-blue-500/10 p-3 text-center">
            <div className="text-xs text-blue-200/70">Utilisés</div>
            <div className="text-lg font-bold text-blue-300" data-testid="mymouv-promo-used">{usedCount}</div>
          </div>
        </div>

        {/* Generate form */}
        <div className="rounded-xl bg-white/5 border border-white/10 p-3 mb-4">
          <div className="flex flex-wrap items-end gap-2">
            <div className="w-24">
              <label className="block text-[11px] text-white/60 mb-1">Nombre</label>
              <input type="number" min="1" max="50" value={count} onChange={e => setCount(e.target.value)} className="w-full bg-white/10 border border-white/15 rounded-lg px-2 py-1.5 text-sm" data-testid="mymouv-promo-count" />
            </div>
            <div className="flex-1 min-w-[10rem]">
              <label className="block text-[11px] text-white/60 mb-1">Restreindre à un type de billet (optionnel)</label>
              <select value={ttypeId} onChange={e => setTtypeId(e.target.value)} className="w-full bg-white/10 border border-white/15 rounded-lg px-2 py-1.5 text-sm" data-testid="mymouv-promo-ttype">
                <option value="">Tous les types</option>
                {(event?.ticket_types || []).map(t => (
                  <option key={t.id} value={t.id}>{t.name} · {t.price} {t.currency}{t.is_vip ? ' · VIP' : ''}</option>
                ))}
              </select>
            </div>
            <button onClick={generate} disabled={busy} className="px-4 py-2 rounded-lg bg-gradient-to-r from-fuchsia-500 to-purple-600 hover:from-fuchsia-600 hover:to-purple-700 text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50" data-testid="mymouv-promo-generate">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Générer
            </button>
          </div>
        </div>

        {/* List */}
        <div className="max-h-[45vh] overflow-y-auto pr-1">
          {loading ? (
            <Loader2 className="w-6 h-6 animate-spin mx-auto my-6" />
          ) : codes.length === 0 ? (
            <div className="text-center py-8 text-white/40 border border-dashed border-white/10 rounded-xl">
              <Ticket className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">Aucun code promo pour cet événement.</p>
            </div>
          ) : (
            <ul className="space-y-2" data-testid="mymouv-promo-list">
              {codes.map(c => (
                <li key={c.id} className="rounded-lg bg-white/5 border border-white/10 p-3 flex items-center gap-3" data-testid={`mymouv-promo-item-${c.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono font-bold text-fuchsia-200 tracking-widest">{c.code}</span>
                      <StatusPill status={c.status} />
                      {c.ticket_type_id && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white/60">
                          {(event?.ticket_types || []).find(t => t.id === c.ticket_type_id)?.name || 'Type spécifique'}
                        </span>
                      )}
                    </div>
                    {c.status === 'used' && c.used_at && (
                      <div className="text-[11px] text-white/50 mt-0.5">Utilisé le {c.used_at.slice(0, 16).replace('T', ' ')}</div>
                    )}
                  </div>
                  <button onClick={() => copy(c.code)} className="p-1.5 rounded-lg hover:bg-white/10" title="Copier" data-testid={`mymouv-promo-copy-${c.id}`}>
                    <Copy className="w-4 h-4" />
                  </button>
                  {c.status === 'active' && (
                    <button onClick={() => remove(c.id)} className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-300" title="Supprimer" data-testid={`mymouv-promo-delete-${c.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyMouvPromoCodesPanel;
